import { useMemo, useState } from 'react';
import { BookOpen, Check, ExternalLink } from 'lucide-react';
import { FloatingStatuteCitation } from './FloatingStatuteCitation';
import { useStatutes } from '../hooks/useStatutes';
import { formatAuthorityChipLabel } from '../lib/formatAuthorityLabel';
import type { Source } from '../lib/ai';

interface SourcesProps {
  sources: Source[];
  /** Compact = single row, used inline under chat bubbles. */
  variant?: 'compact' | 'panel';
  /** Optional heading text shown above the chip row. */
  label?: string;
  /** Live mode — show checkmarks as authorities arrive during streaming. */
  live?: boolean;
}

function isWebSource(source: Source): boolean {
  return source.sourceType === 'web' || Boolean(source.url?.trim());
}

/**
 * Citation chips rendered under every grounded AI response. Statute chips open
 * the cited section in a small floating reader; web chips open the source URL.
 */
export function Sources({ sources, variant = 'compact', label, live = false }: SourcesProps) {
  const { statutes } = useStatutes();
  const [openSource, setOpenSource] = useState<Source | null>(null);

  const statuteTitleById = useMemo(
    () => Object.fromEntries(statutes.map((statute) => [statute.id, statute.title])),
    [statutes],
  );

  if (!sources || sources.length === 0) return null;

  const librarySources = sources.filter((s) => !isWebSource(s));
  const webSources = sources.filter((s) => isWebSource(s));
  const heading =
    label ??
    (variant === 'panel'
      ? 'Authorities cited'
      : live
        ? 'Authorities'
        : webSources.length > 0 && librarySources.length === 0
          ? 'Web sources'
          : 'Sources');

  return (
    <>
      <div className={variant === 'panel' ? 'mt-4' : 'mt-2'}>
        <div className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-[#64748B] mb-1.5">
          <BookOpen className="w-3 h-3" />
          <span>{heading}</span>
        </div>
        <div className="flex flex-wrap gap-1.5">
          {librarySources.map((source, idx) => {
            const { primary, secondary } = formatAuthorityChipLabel(source, statuteTitleById);
            const chipClass =
              'inline-flex items-center gap-1 px-2 py-1 rounded-md bg-primary/10 text-primary text-xs font-medium transition-colors max-w-full';
            const titleText = secondary ? `${primary} — ${secondary}` : primary;

            if (!source.statuteId) {
              return (
                <span
                  key={`lib-${source.statuteId}-${source.sectionId || ''}-${idx}`}
                  title={source.snippet || titleText}
                  className={chipClass}
                >
                  <span className="font-semibold truncate max-w-[280px]">{primary}</span>
                  {secondary ? (
                    <span className="text-primary/70 truncate max-w-[160px]">— {secondary}</span>
                  ) : null}
                </span>
              );
            }

            return (
              <button
                key={`lib-${source.statuteId}-${source.sectionId || ''}-${idx}`}
                type="button"
                title={source.snippet || titleText}
                onClick={() => setOpenSource(source)}
                className={`${chipClass} hover:bg-primary/20 cursor-pointer text-left ${live ? 'animate-in fade-in duration-300' : ''}`}
              >
                {live ? <Check className="w-3 h-3 shrink-0 text-emerald-500" strokeWidth={3} /> : null}
                <span className="font-semibold truncate max-w-[280px]">{primary}</span>
                {secondary ? (
                  <span className="text-primary/70 truncate max-w-[160px]">— {secondary}</span>
                ) : null}
              </button>
            );
          })}
          {webSources.map((source, idx) => {
            const title = source.title || source.label || source.url || 'Web source';
            const chipClass =
              'inline-flex items-center gap-1 px-2 py-1 rounded-md bg-slate-500/10 text-slate-700 dark:text-slate-200 text-xs font-medium transition-colors max-w-full hover:bg-slate-500/20';
            if (source.url) {
              return (
                <a
                  key={`web-${source.url}-${idx}`}
                  href={source.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  title={source.snippet || title}
                  className={chipClass}
                >
                  {live ? <Check className="w-3 h-3 shrink-0 text-emerald-500" strokeWidth={3} /> : null}
                  <ExternalLink className="w-3 h-3 shrink-0 opacity-70" />
                  <span className="font-semibold truncate max-w-[280px]">{title}</span>
                </a>
              );
            }
            return (
              <span key={`web-${idx}`} title={source.snippet || title} className={chipClass}>
                <span className="font-semibold truncate max-w-[280px]">{title}</span>
              </span>
            );
          })}
        </div>
        {webSources.length > 0 ? (
          <p className="mt-2 text-[11px] leading-snug text-[#64748B]">
            Web sources are supplementary — verify against the official gazette or court rules before
            relying on them in filings.
          </p>
        ) : null}
      </div>
      {openSource ? (
        <FloatingStatuteCitation source={openSource} onClose={() => setOpenSource(null)} />
      ) : null}
    </>
  );
}
